import { useState } from 'react';
import { Button, Field, Modal } from '@/components/ui';
import { API_URL, tokens } from '@/lib/api';

interface Result {
  provider: string;
  transcript: string;
  confidence: number | null;
  audio_seconds: number;
  elapsed_ms: number;
  errors: string[];
}

/**
 * Run one recording through one speech provider and show what it heard.
 *
 * The way to judge Glee Local STT before moving a client onto it: the same
 * call recording through Deepgram and through Glee Local, side by side.
 * Results are kept per provider while the dialog is open so the comparison is
 * on one screen.
 */
export function SttTest({
  provider,
  onClose,
}: {
  provider: { id: string; name: string } | null;
  onClose: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, Result>>({});

  const run = async () => {
    if (!provider || !file) return;
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${API_URL}/admin/providers/${provider.id}/stt-test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokens.access() ?? ''}` },
        body,
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error?.message ?? json?.detail ?? `HTTP ${response.status}`);
      setResults((r) => ({ ...r, [provider.id]: json as Result }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={provider !== null}
      onClose={onClose}
      title={`Test ${provider?.name ?? 'speech recognition'}`}
      description="Upload a mono 16-bit WAV — a call recording from the portal works. It is streamed in 20 ms frames, exactly as a live call would be."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={run} loading={busy} disabled={!file}>
            Transcribe
          </Button>
        </>
      }
      wide
    >
      <Field label="Recording (.wav)">
        <input
          type="file"
          accept=".wav,audio/wav"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-ink-700 file:mr-3 file:rounded-md file:border file:border-line file:bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium dark:text-ink-200"
        />
      </Field>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div className="mt-4 space-y-3">
        {Object.values(results).map((r) => (
          <div key={r.provider} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-ink-900 dark:text-ink-50">{r.provider}</span>
              <span className="text-xs text-ink-500">
                {r.audio_seconds}s of audio · {(r.elapsed_ms / 1000).toFixed(1)}s to process
                {r.confidence !== null && ` · confidence ${Math.round(r.confidence * 100)}%`}
              </span>
            </div>
            <p className="mt-2 text-sm text-ink-800 dark:text-ink-100">
              {r.transcript || <span className="italic text-ink-400">heard nothing</span>}
            </p>
            {r.errors.length > 0 && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{r.errors.join('; ')}</p>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
